import {
  forwardRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react'

/**
 * Базовые элементы интерфейса на общих токенах (ДС-07).
 *
 * Замещаются компонентами Horizon UI при подключении шаблона; до тех пор
 * оформление собрано здесь, чтобы не расползаться по страницам.
 */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
}

const buttonStyles = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 border-transparent',
  secondary:
    'bg-white text-ink-700 border-ink-300 hover:border-brand-500 hover:text-brand-600 dark:bg-ink-900 dark:text-ink-300 dark:border-ink-700',
  ghost: 'bg-transparent text-ink-600 border-transparent hover:text-brand-600',
  danger:
    'bg-white text-danger-700 dark:text-danger-100 border-danger-500/40 hover:bg-danger-100 dark:bg-ink-900',
}

export function Button({ variant = 'secondary', size = 'md', className = '', ...rest }: ButtonProps) {
  // Обычная кнопка живёт в одной строке с полями и списками, поэтому
  // берёт общую коробку. Малая — для мест внутри таблиц и строк, где
  // ростом с поле она была бы неуместна.
  const box =
    size === 'sm'
      ? 'inline-flex items-center rounded-md border px-2.5 py-1 text-xs'
      : 'control control-button'
  return (
    <button
      {...rest}
      className={`gap-1.5 font-medium disabled:cursor-not-allowed disabled:opacity-50 ${box} ${buttonStyles[variant]} ${className}`}
    />
  )
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string
  hint?: ReactNode
  error?: string
  children: ReactNode
}) {
  return (
    // Три строки: подпись, орган управления, пояснение. Поле участвует
    // в строках родительской сетки, поэтому в ряду из нескольких полей
    // подписи стоят вровень с подписями, а поля с полями — независимо
    // от того, у кого из них есть пояснение, а у кого нет.
    <label className="field">
      <span className="block text-sm font-medium text-ink-700 dark:text-ink-300">{label}</span>
      {children}
      {error ? (
        <span className="block text-xs text-danger-700 dark:text-danger-100" role="alert">
          {error}
        </span>
      ) : (
        // Строка пояснения занимает место всегда: иначе поле без него
        // оказывалось бы ниже соседнего, и ряд шёл вразнобой.
        <span className="block text-xs text-ink-500">{hint}</span>
      )}
    </label>
  )
}

// Расцветка полей ввода, списков и выбора файла. Отделена от метрик:
// коробка одна на все органы управления, а цвет у полей свой.
const fieldSkin =
  'border-ink-300 bg-white text-ink-900 shadow-xs placeholder:text-ink-400 ' +
  'focus:border-brand-500 disabled:opacity-60 ' +
  'dark:border-ink-700 dark:bg-ink-900 dark:text-ink-50'

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = '', ...rest }, ref) {
    return (
      <input
        ref={ref}
        {...rest}
        className={`control w-full ${fieldSkin} ${className}`}
      />
    )
  },
)

export function Checkbox({
  label,
  hint,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        {...rest}
        className="mt-0.5 size-4 shrink-0 rounded border-ink-300 text-brand-600 dark:border-ink-700"
      />
      <span>
        <span className="block text-sm text-ink-700 dark:text-ink-300">{label}</span>
        {hint && <span className="block text-xs text-ink-500">{hint}</span>}
      </span>
    </label>
  )
}

/** Сообщение об ошибке или успехе поверх раздела (ДС-11). */
export function Notice({
  kind,
  children,
  onClose,
}: {
  kind: 'error' | 'success' | 'warning'
  children: ReactNode
  onClose?: () => void
}) {
  const styles = {
    error: 'border-danger-500/30 bg-danger-100 dark:bg-danger-700/25 text-danger-700 dark:text-danger-100',
    success: 'border-success-500/30 bg-success-100 dark:bg-success-700/25 text-success-700 dark:text-success-100',
    warning: 'border-warning-500/30 bg-warning-100 dark:bg-warning-700/25 text-warning-700 dark:text-warning-100',
  }
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      className={`mb-4 flex items-start gap-3 rounded-md border px-3 py-2 text-sm ${styles[kind]}`}
    >
      <span className="flex-1">{children}</span>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Закрыть сообщение"
          className="shrink-0 opacity-60 hover:opacity-100"
        >
          ✕
        </button>
      )}
    </div>
  )
}

/** Пустое состояние с подсказкой о следующем действии (ДС-10). */
export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string
  hint: string
  action?: ReactNode
}) {
  return (
    <div className="rounded-lg border border-dashed border-ink-300 px-6 py-10 text-center dark:border-ink-700">
      <p className="text-sm font-medium text-ink-700 dark:text-ink-300">{title}</p>
      <p className="mx-auto mt-1.5 max-w-md text-sm text-ink-500">{hint}</p>
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  )
}

/** Необратимая операция требует явного подтверждения намерения (ДС-11). */
export function ConfirmDialog({
  title,
  message,
  confirmLabel,
  onConfirm,
  onCancel,
  pending,
}: {
  title: string
  message: ReactNode
  confirmLabel: string
  onConfirm: () => void
  onCancel: () => void
  pending?: boolean
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-950/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-md rounded-lg border border-ink-200 bg-white p-5 shadow-lg dark:border-ink-800 dark:bg-ink-900">
        <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
        <div className="mt-2 text-sm text-ink-600 dark:text-ink-400">{message}</div>
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onCancel} disabled={pending}>
            Отмена
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={pending}>
            {pending ? 'Выполняется…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}

export function Spinner({ label }: { label: string }) {
  return <p className="py-8 text-center text-sm text-ink-500">{label}</p>
}

/**
 * Список выбора.
 *
 * Существует ради одного: чтобы список стоял вровень с полем ввода
 * и кнопкой в той же строке. Написанный руками `<select>` этого
 * не обеспечивает — браузер рисует его по-своему.
 */
export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className = '', ...rest }, ref) {
    return (
      <select ref={ref} {...rest} className={`control control-select w-full ${fieldSkin} ${className}`} />
    )
  },
)

/** Выбор файла: та же коробка, что и у прочих полей. */
export const FileInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function FileInput({ className = '', ...rest }, ref) {
    return (
      <input
        ref={ref}
        {...rest}
        type="file"
        className={`control control-file w-full ${fieldSkin} ${className}`}
      />
    )
  },
)
